import { getDefaultChatRequestBody } from '@/pages/DigitalHuman/util/util'
import axios, { AxiosRequestConfig } from 'axios'

export interface TTSRequestParams {
  appId: string
  id: string
  text: string
  option: {
    needTts: boolean
    needBs: boolean
  }
}

export interface TTSResponse {
  code: number
  data: {
    bs: string
    tts: string
    text: string
  }
  requestId: string
  success: boolean
  message: string
}

export interface TTSConfig {
  apiUrl?: string
  appId?: string
  timeout?: number
  retryCount?: number
}

// 默认配置
const DEFAULT_CONFIG: Required<TTSConfig> = {
  apiUrl: 'https://vlog-zhuangyou.shuwen.com/api/qaRobot/new/render',
  appId: 'debug',
  timeout: 30000, // 30秒超时
  retryCount: 2 // 重试2次
}

/**
 * 调用TTS API生成语音和表情数据
 */
export const generateTTS = async (
  params: TTSRequestParams, 
  config: TTSConfig = {}
): Promise<TTSResponse> => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config }
  
  const requestConfig: AxiosRequestConfig = {
    method: 'post',
    url: finalConfig.apiUrl,
    headers: {
      'Content-Type': 'application/json',
      'User-Agent': 'digital-human-app/1.0.0'
    },
    data: params,
    timeout: finalConfig.timeout
  }

  let lastError: any
  
  // 重试逻辑
  for (let attempt = 0; attempt <= finalConfig.retryCount; attempt++) {
    try {
      console.log(`TTS API调用尝试 ${attempt + 1}/${finalConfig.retryCount + 1}`)
      
      const response = await axios(requestConfig)
      
      // 验证响应数据
      if (!response.data) {
        throw new Error('API返回数据为空')
      }
      
      const result: TTSResponse = response.data
      
      // 验证业务逻辑成功
      if (!result.success || result.code !== 200) {
        throw new Error(`API业务错误: ${result.message} (code: ${result.code})`)
      }
      
      // 验证必要字段
      if (!result.data || !result.data.tts || !result.data.bs) {
        throw new Error('API返回数据不完整，缺少tts或bs字段')
      }
      
      console.log('TTS API调用成功')
      return result
      
    } catch (error: any) {
      lastError = error
      console.error(`TTS API调用失败 (尝试 ${attempt + 1}):`, error.message)
      
      // 如果是最后一次尝试，或者是不可重试的错误，直接抛出
      if (attempt === finalConfig.retryCount || isNonRetryableError(error)) {
        break
      }
      
      // 等待一段时间后重试
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
    }
  }
  
  throw lastError
}

/**
 * 判断是否为不可重试的错误
 */
function isNonRetryableError(error: any): boolean {
  // 4xx错误通常是客户端错误，不应该重试
  if (error.response && error.response.status >= 400 && error.response.status < 500) {
    return true
  }
  
  // 参数错误等不应该重试
  if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
    return true
  }
  
  return false
}

/**
 * 生成TTS请求的默认参数
 */
export const createTTSParams = (
  text: string, 
  id?: string, 
  appId: string = DEFAULT_CONFIG.appId
): TTSRequestParams => {
  return {
    appId,
    id: getDefaultChatRequestBody().id || `tts_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    text: text.trim(),
    option: {
      needTts: true,
      needBs: true
    }
  }
}

/**
 * 验证文本是否适合TTS处理
 */
export const validateTTSText = (text: string): { valid: boolean; message?: string } => {
  if (!text || typeof text !== 'string') {
    return { valid: false, message: '文本不能为空' }
  }
  
  const trimmedText = text.trim()
  if (trimmedText.length === 0) {
    return { valid: false, message: '文本不能为空' }
  }
  
  if (trimmedText.length > 1000) {
    return { valid: false, message: '文本长度不能超过1000个字符' }
  }
  
  return { valid: true }
} 
